import { useState, useEffect } from 'react';

const TRIGGER_EVENTS = [
  // Onboarding lifecycle (fired by VTTD frontend)
  'onboarding_started', 'onboarding_completed', 'onboarding_skipped', 'onboarding_path_selected',
  // User auth lifecycle (fired by VTTD frontend)
  'user_signed_up', 'user_signed_in', 'user_signed_out',
  // Project activity (fired by VTTD frontend)
  'project_created', 'project_opened', 'project_completed', 'project_exported',
  'project_limit_reached',
  'project_completed_first', 'project_completed_power',
  // AI features (fired by VTTD frontend)
  'ai_chat_started', 'ai_chat_message_sent',
  // Content & uploads (fired by VTTD frontend)
  'document_uploaded', 'page_viewed',
  // Billing — PostHog events (fired by VTTD frontend)
  'checkout_started', 'checkout_completed', 'checkout_canceled',
  'subscription_cancelled', 'subscription_resubscribed',
  // Billing — Stripe webhook events (fired by Mercury gateway)
  'subscription_created', 'payment_failed', 'invoice_paid',
  // Team (fired by VTTD frontend)
  'team_invite_sent', 'team_invite_resent', 'team_invite_accepted', 'team_invite_revoked',
  // Engagement (fired by VTTD frontend)
  'feedback_submitted', 'time_saved_milestone',
  'upgrade_prompt_shown', 'project_limit_warning',
  // Internally generated (Mercury cron Lambdas — not from PostHog)
  'user_inactive_7d', 'subscription_created_14d',
];

const CONDITION_FIELDS = [
  // User profile / counters
  'projectsStarted', 'projectsCompleted', 'sessionCount',
  'teamMembersInvited', 'planType', 'subscriptionStatus',
  // Onboarding
  'onboardingPath', 'fields_populated',
  // Activity
  'loginMethod', 'signup_method', 'projectType', 'project_id', 'exportFormat',
  // AI
  'field_key', 'current_step',
  // Page context
  'page_name', 'page_category', 'page_path', 'is_public',
  // Billing
  'pricingTier',
];

const OPERATORS = [
  { value: 'eq', label: '=' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'neq', label: '!=' },
];

const WAIT_UNITS = [
  { value: 60, label: 'minutes' },
  { value: 3600, label: 'hours' },
  { value: 86400, label: 'days' },
];

function InputField({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-cream/70 mb-1">{label}</label>
      {children}
    </div>
  );
}

const inputClass = 'w-full px-3 py-2 rounded-xl border-2 border-ink/30 bg-background text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary';
const selectClass = inputClass;

export default function NodeEditor({ step, templates, onUpdate, onDelete, onClose }) {
  const [local, setLocal] = useState({ ...step });

  // Sync when step changes
  useEffect(() => {
    setLocal({ ...step });
  }, [step]);

  const handleChange = (field, value) => {
    const updated = { ...local, [field]: value };
    setLocal(updated);
    onUpdate(updated);
  };

  // Wait helpers
  const waitUnit = local.duration >= 86400 ? 86400 : local.duration >= 3600 ? 3600 : 60;
  const waitAmount = local.duration ? Math.round(local.duration / waitUnit) : 1;

  const handleWaitChange = (amount, unit) => {
    const duration = amount * unit;
    const labels = { 60: 'minutes', 3600: 'hours', 86400: 'days' };
    const label = `${amount} ${labels[unit] || 'seconds'}`;
    const updated = { ...local, duration, label };
    setLocal(updated);
    onUpdate(updated);
  };

  return (
    <div className="fixed right-0 top-0 h-full w-80 bg-ink-light border-l-2 border-ink shadow-brutal-lg z-30 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <h3 className="text-sm font-bold text-cream capitalize">
          {step.type} Properties
        </h3>
        <button
          onClick={onClose}
          className="text-cream/50 hover:text-cream transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="p-4 space-y-4">
        {/* Trigger */}
        {step.type === 'trigger' && (
          <InputField label="Event">
            <select
              value={local.event || ''}
              onChange={(e) => handleChange('event', e.target.value)}
              className={selectClass}
            >
              <option value="">Select event...</option>
              {TRIGGER_EVENTS.map(ev => (
                <option key={ev} value={ev}>{ev}</option>
              ))}
            </select>
          </InputField>
        )}

        {/* Email */}
        {step.type === 'email' && (
          <>
            <InputField label="Name">
              <input
                type="text"
                value={local.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                className={inputClass}
                placeholder="Email name"
              />
            </InputField>
            <InputField label="Template">
              <select
                value={local.templateId || ''}
                onChange={(e) => handleChange('templateId', e.target.value)}
                className={selectClass}
              >
                <option value="">Select template...</option>
                {(templates || []).map(t => (
                  <option key={t.id} value={t.id}>{t.subject || t.id}</option>
                ))}
              </select>
            </InputField>
            <InputField label="Step Number">
              <input
                type="number"
                value={local.stepNumber || ''}
                onChange={(e) => handleChange('stepNumber', parseInt(e.target.value, 10) || 0)}
                className={inputClass}
                min={1}
              />
            </InputField>
          </>
        )}

        {/* Wait */}
        {step.type === 'wait' && (
          <div className="flex gap-2">
            <InputField label="Duration">
              <input
                type="number"
                value={waitAmount}
                onChange={(e) => handleWaitChange(parseInt(e.target.value, 10) || 1, waitUnit)}
                className={inputClass}
                min={1}
              />
            </InputField>
            <InputField label="Unit">
              <select
                value={waitUnit}
                onChange={(e) => handleWaitChange(waitAmount, parseInt(e.target.value, 10))}
                className={selectClass}
              >
                {WAIT_UNITS.map(u => (
                  <option key={u.value} value={u.value}>{u.label}</option>
                ))}
              </select>
            </InputField>
          </div>
        )}

        {/* Condition */}
        {step.type === 'condition' && (
          <>
            <InputField label="Label">
              <input
                type="text"
                value={local.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                className={inputClass}
                placeholder="e.g., Started a project?"
              />
            </InputField>
            <InputField label="Field">
              <select
                value={local.field || ''}
                onChange={(e) => handleChange('field', e.target.value)}
                className={selectClass}
              >
                <option value="">Select field...</option>
                {CONDITION_FIELDS.map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </InputField>
            <div className="flex gap-2">
              <InputField label="Operator">
                <select
                  value={local.operator || 'eq'}
                  onChange={(e) => handleChange('operator', e.target.value)}
                  className={selectClass}
                >
                  {OPERATORS.map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </select>
              </InputField>
              <InputField label="Value">
                <input
                  type="text"
                  value={local.value !== undefined ? String(local.value) : ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    const parsed = isNaN(v) ? v : Number(v);
                    handleChange('value', parsed);
                  }}
                  className={inputClass}
                />
              </InputField>
            </div>
          </>
        )}

        {/* Exit */}
        {step.type === 'exit' && (
          <InputField label="Reason (optional)">
            <input
              type="text"
              value={local.reason || ''}
              onChange={(e) => handleChange('reason', e.target.value)}
              className={inputClass}
              placeholder="e.g., Converted to paid"
            />
          </InputField>
        )}

        {/* Delete */}
        {step.type !== 'trigger' && (
          <div className="pt-4 border-t border-white/10">
            <button
              onClick={() => onDelete(step.id)}
              className="w-full px-4 py-2 text-sm font-semibold text-white bg-alert rounded-xl border-2 border-ink shadow-brutal hover:-translate-y-0.5 hover:shadow-brutal-lg active:translate-y-0.5 transition-all duration-200"
            >
              Remove Step
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
